import * as React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { clsHelper } from '../../utilities/class-name-helper';
import { injectScript } from '../../utilities/inject-helper';

import { RichTextComponent, RichText, IImageData, Image } from '@msdyn365-commerce/core';
import { Button, Heading, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';

import { IFormInputsData, FormInputsFieldType, IOptionsData, ISimpleFormProps } from './simple-form.props.autogenerated';
import { SimpleFormInput, SimpleFormDataAction } from './actions/simple-form-submit-action';

//==============================================================================
// CLASS NAME UTILITY
//==============================================================================
const BASE_CLASS = 'simple-form';
const cls = (fragment: string) => clsHelper(BASE_CLASS, fragment);

//==============================================================================
// CLASS DEFINITION
//==============================================================================
/**
 * SimpleForm component
 * @extends {React.Component<ISimpleFormProps<{}>>}
 */
//==============================================================================
@observer
class SimpleForm extends React.Component<ISimpleFormProps<{}>> {
    //==========================================================================
    // VARIABLES
    //==========================================================================
    @observable private modalIsOpen = true;
    @observable private statusMessages: string[] = [];
    @observable private statusMessageClass: string = '';
    @observable private isSubmitting: boolean = false;
    @observable private formSuccess: boolean = false;
    private readonly recaptchaSiteKey?: string = this.props.config.recaptchaSiteKey;

    private formData = {};

    private validationFields: {
        id: string,
        name: string,
        fieldType: string,
        isRequired: boolean,
        regex: string,
        error: string
    }[] = [];

    private confirmEmailField: IFormInputsData;

    //==========================================================================
    // PUBLIC METHODS
    //==========================================================================
    //------------------------------------------------------
    // Constructor
    //------------------------------------------------------
    constructor(props: ISimpleFormProps<{}>) {
        super(props);

        props.config.formInputs?.forEach(input => {
            this.formData[input.id] = '';
            if (input.isRequired || input.regex) {
                this.validationFields.push({
                    id: input.id,
                    name: input.label,
                    fieldType: input.fieldType,
                    isRequired: !!input.isRequired,
                    regex: input.regex || '',
                    error: input.error || ''
                });
            }
        });

        this.confirmEmailField = {
            id: 'confirmEmail',
            label: props.resources.simpleForm_confirmEmailLabel,
            fieldType: 'email' as FormInputsFieldType,
            isRequired: true,
            className: 'email-confirm'
        };
    }

    //------------------------------------------------------
    // Invoked immediately after component is mounted
    //------------------------------------------------------
    public componentDidMount(): void {
        // Only injects reCAPTCHA script if site key is provided in config
        if (this.recaptchaSiteKey) {
            injectScript(`https://www.google.com/recaptcha/api.js?render=${this.recaptchaSiteKey}`);
        }
    }

    //------------------------------------------------------
    // Render function
    //------------------------------------------------------
    public render(): JSX.Element | null {
        const isModal = this.props.config.isModal;

        if (isModal) {
            return this._renderModal();
        }

        return this._renderForm();

    }

    //==========================================================================
    // PRIVATE METHODS
    //==========================================================================
    //------------------------------------------------------
    // Close modal if modal mode is selected
    //------------------------------------------------------
    private readonly _closeModal = (): void => {
        this.modalIsOpen = false;
        const cookies = this.props.context.request.cookies;
        const maxAge = this.props.config.maxAge ? this.props.config.maxAge * 24 * 60 * 60 : undefined;
        cookies.set(this.props.config.cookieName!, true, {maxAge: maxAge});
    };

    //------------------------------------------------------
    // Validate form before submit
    //------------------------------------------------------
    private readonly _validateForm = (): string[] => {
        const { config, resources } = this.props;
        const validationErrors: string[] = [];

        this.validationFields.forEach(field => {
            if (!this.formData[field.id] && field.isRequired) {
                validationErrors.push(`${field.name} ${resources.simpleForm_isRequiredMessage}`);
            } else if (this.formData[field.id] && field.regex) {
                // eslint-disable-next-line security/detect-non-literal-regexp
                const regex = new RegExp(field.regex);
                if (!regex.test(this.formData[field.id])) {
                    validationErrors.push(field.error);
                }
            }

            if (field.fieldType === 'email' && config.confirmEmail && this.formData[field.id] !== this.formData[this.confirmEmailField.id]) {
                validationErrors.push(resources.simpleForm_confirmEmailError);
            }
        });

        return validationErrors;
    };

    //------------------------------------------------------
    // Set success or error message
    //------------------------------------------------------
    private readonly _setStatusMessage = (messages: string[], isSuccess: boolean): void => {
        this.statusMessages = messages;
        this.statusMessageClass = isSuccess ? 'success' : 'error';
        if (!isSuccess) { return; }

        // Only set a timeout for success message if modal mode is true
        if (this.props.config.isModal) {
            setTimeout(() => {
                this.statusMessages = [];
                this.modalIsOpen = !isSuccess;
            }, this.props.config.messageTimeout || 3000);
        }
    };

    //------------------------------------------------------
    // Render modal if modal mode is selected
    //------------------------------------------------------
    private readonly _renderModal = (): JSX.Element | null => {
        const cookies = this.props.context.request.cookies;
        if (!cookies.isConsentGiven() || cookies.get(this.props.config.cookieName!).value) {
            return null;
        }

        return (
            <Modal
                autoFocus={true}
                fade={false}
                applicationNode={'rsg-root'}
                isOpen={this.modalIsOpen}
                toggle={this._closeModal}
                className={cls('modal')}
                horizontalPosition={'center'}
                verticalPosition={'center'}
            >
                <ModalHeader toggle={this._closeModal} tag={'div'} />
                <ModalBody>
                    {this._renderForm()}
                </ModalBody>
                <ModalFooter />
            </Modal>
        );
    };

    //------------------------------------------------------
    // Render form
    //------------------------------------------------------
    private readonly _renderForm = (): JSX.Element => {
        const { config, resources, slots } = this.props;
        const hasAdditionalContent = !!slots?.additionalContent?.length;
        const useRecaptcha = !!this.recaptchaSiteKey;

        return (
            <div className={classnames(BASE_CLASS, config.className)}>

                <div className={cls('container')}>
                    {config.logoImage && this._renderImage(config.logoImage, this.props, cls('logo'))}
                    {config.title && <Heading className={cls('title')} headingTag={config.title.tag} text={config.title.text} />}

                    {/* Do not render form after successfully submitting it */}
                    {!this.formSuccess && (
                        <>
                            {config.headerText && this._renderRichText(config.headerText, cls('header'))}
                            <div className={cls('form')}>
                                <div className={cls('inputs')}>
                                    {this.props.config.formInputs?.map((input: IFormInputsData, index: number) => (
                                        <React.Fragment key={index}>
                                            {this._renderInputGroup(input)}
                                            {input.fieldType === 'email' && config.confirmEmail && this._renderInputGroup(this.confirmEmailField)}
                                        </React.Fragment>
                                    ))}
                                </div>
                                {config.footerText && this._renderRichText(config.footerText, cls('footer'))}
                                <Button
                                    className={classnames(cls('button'), {'disabled': this.isSubmitting}, {'g-recaptcha': useRecaptcha})}
                                    aria-label={config.submitText || resources.simpleForm_submitButton}
                                    data-sitekey={this.recaptchaSiteKey}
                                    data-size={useRecaptcha ? 'invisible': undefined}
                                    onClick={() => this._onSubmit(this.recaptchaSiteKey)}
                                    disabled={this.isSubmitting}
                                >
                                    {config.submitText || resources.simpleForm_submitButton}
                                </Button>
                            </div>
                        </>
                    )}

                    {!!this.statusMessages.length && this._renderMessages()}

                    {config.postFooterText && this._renderRichText(config.postFooterText, cls('post-footer'))}
                </div>

                {hasAdditionalContent && this._renderSlotItems(slots.additionalContent, cls('content'))}

            </div>
        );
    };

    //------------------------------------------------------
    // Render each input group
    //------------------------------------------------------
    private readonly _renderInputGroup = (input: IFormInputsData): JSX.Element => {
        const fieldMap = {
            string:     this._renderInput,
            email:      this._renderInput,
            checkbox:   this._renderInput,
            select:     this._renderSelect,
            textarea:   this._renderTextarea
        };

        return (
            <div className={classnames(cls('input-group'), input.className, {'required': input.isRequired})}>
                <label className={cls('label')} htmlFor={input.id}>{input.label}</label>
                {fieldMap[input.fieldType](input)}
            </div>
        );
    };

    //------------------------------------------------------
    // Render input element
    //------------------------------------------------------
    private readonly _renderInput = (input: IFormInputsData): JSX.Element => {
        return (
            <input className={classnames(cls('input'), input.fieldType)} type={input.fieldType} onChange={this._onInputChange} id={input.id} placeholder={input.placeholder} />
        );
    };

    //------------------------------------------------------
    // Render select dropdown element
    //------------------------------------------------------
    private readonly _renderSelect = (input: IFormInputsData): JSX.Element => {
        // If option has not been selected, assign first option value as default
        if (!this.formData[input.id] && input.options?.length) {
            this.formData[input.id] = input.options[0].value;
        }

        return (
            <select className={classnames(cls('select'), input.fieldType)} onChange={this._onInputChange} id={input.id} name={input.id}>
                {input.options?.map((option: IOptionsData, index: number) => (
                    <option value={option.value} key={index}>{option.label}</option>
                ))}
            </select>
        );
    };

    //------------------------------------------------------
    // Render textarea element
    //------------------------------------------------------
    private readonly _renderTextarea = (input: IFormInputsData): JSX.Element => {
        return (
            <textarea className={classnames(cls('textarea'), input.fieldType)} onChange={this._onInputChange} id={input.id} name={input.id} />
        );
    };

    //------------------------------------------------------
    // Render image
    //------------------------------------------------------
    private readonly _renderImage = (image: IImageData, props: ISimpleFormProps<{}>, className?: string): JSX.Element => {
        return (
            <div className={className}>
                <Image
                    {...image}
                    gridSettings={props.context.request.gridSettings ?? {}}
                    imageSettings={image?.imageSettings}
                />
            </div>
        );
    };

    //------------------------------------------------------
    // Render rich text
    //------------------------------------------------------
    private readonly _renderRichText = (text: RichText, className?: string): JSX.Element => {
        return (
            <div className={className}>
                <RichTextComponent text={text} className={`${className}-text`} />
            </div>
        );
    };

    //------------------------------------------------------
    // Render success or error messages
    //------------------------------------------------------
    private readonly _renderMessages = (): JSX.Element => {
        return (
            <div className={classnames(cls('messages'), this.statusMessageClass)}>
                {this.statusMessages.map((message, index) => {
                    return (
                        <span key={index} className={cls('message')}>
                            {message}
                        </span>
                    );
                })}
            </div>
        );
    };

    //------------------------------------------------------
    // Render slot items
    //------------------------------------------------------
    private readonly _renderSlotItems = (items: React.ReactNode[], className?: string): JSX.Element => {
        return (
            <div className={className}>
                {items.map((slot: React.ReactNode, index: number) => (
                    <React.Fragment key={index}>
                        {slot}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    //------------------------------------------------------
    // On input change
    //------------------------------------------------------
    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>): void => {
        this.formData[event.currentTarget.id] = event.currentTarget.value;

        // Remove status messages if there are any when fields are being updated
        if (this.statusMessages.length) {
            this.statusMessages = [];
        }
    };

    //------------------------------------------------------
    // Submit form
    //------------------------------------------------------
    private readonly _submitForm = async (token?: string): Promise<void> => {
        const { config, context, resources } = this.props;
        const request = context.request;

        if (request && config.url) {
            this.isSubmitting = true;

            if (token) {
                this.formData['token'] = token;
            }

            const input = new SimpleFormInput(
                this.formData,
                config.url
            );

            await SimpleFormDataAction(input, context.actionContext).then(result => {
                this._setStatusMessage([resources.simpleForm_submitSuccess], true);

                const cookies = context.request.cookies;
                cookies.set(config.cookieName!, true);

                this.formSuccess = true;
            }).catch(() => {
                context.telemetry.error('Failed to submit form');
                this._setStatusMessage([resources.simpleForm_submitError], false);
            });

            this.isSubmitting = false;
        }
    };

    //------------------------------------------------------
    // Verify reCAPTCHA
    //------------------------------------------------------
    private readonly _verifyRecaptcha = async (siteKey: string): Promise<string | void> => {
        if (typeof window !== 'undefined') {
            const { context } = this.props;

            // @ts-ignore: grecaptcha added through script
            return window.grecaptcha?.execute(
                siteKey,
                { action: 'submit' }
            ).then((result: string) => {
                return result;
            }).catch((error: Error) => {
                context.telemetry.error(error.message);
                throw new Error(error.message);
            });
        }
    };

    //------------------------------------------------------
    // On submit
    //------------------------------------------------------
    private readonly _onSubmit = async (siteKey?: string): Promise<void> => {
        // Validate form
        const errors = this._validateForm();
        if (errors.length) {
            this._setStatusMessage(errors, false);
            return;
        }

        // Verify reCAPTCHA
        if (siteKey) {
            const token = await this._verifyRecaptcha(siteKey);
            if (token) {
                this._submitForm(token);
            } else {
                this._setStatusMessage([this.props.resources.simpleForm_recaptchaError], false);
            }
        } else {
            this._submitForm();
        }
    };
}

export default SimpleForm;
